import { Badge, Box, Flex, HStack, Heading, IconButton, Input, Text, useColorModeValue, VStack } from '@chakra-ui/react'
import React, { useState } from 'react'
import { FiArrowLeft } from 'react-icons/fi'
import { useLanguage } from '../../../hooks/utility/useLanguage'
import { ExampleQuestionCard } from '../../cards/ExampleQuestionCard'

interface ExampleQuestionsEditorProps {
  questions: string[],
  addExampleQuestion: (question: string) => void,
  removeExampleQuestion: (question: string) => void,
}

const ExampleQuestionsEditor: React.FC<ExampleQuestionsEditorProps> = ({ questions, addExampleQuestion, removeExampleQuestion}) => {
  const { t } = useLanguage()

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.600')

  // Temporary UI state
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [editingValue, setEditingValue] = useState('')
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [newQuestionValue, setNewQuestionValue] = useState('')

  // Add example question
  const handleAddExampleQuestion = () => {
    const trimmed = newQuestionValue.trim()
    if (!trimmed || questions.includes(trimmed)) return
    addExampleQuestion(trimmed)
    setNewQuestionValue('')
    setIsAddingNew(false)
  }

  // Start editing
  const handleEditStart = (index: number) => {
    setEditingIndex(index)
    setEditingValue(questions[index])
  }

  // Save edit
  const handleEditSave = (index: number) => {
    const trimmed = editingValue.trim()
    if (!trimmed) return
    const oldQuestion = questions[index]
    if (oldQuestion === trimmed) {
      setEditingIndex(null)
      setEditingValue('')
      return
    }
    addExampleQuestion(trimmed)
    setEditingIndex(null)
    setEditingValue('')
  }

  // Cancel edit
  const handleEditCancel = () => {
    setEditingIndex(null)
    setEditingValue('')
  }

  return (
    <VStack spacing={4} align='stretch'>
      <Box>
        <HStack>
          <Heading size='md' color='app.text.primary' fontWeight='bold'>
            {t('createAgent.exampleQuestions')}
          </Heading>
          <Badge variant='outline' colorScheme='gray' size='sm'>
            {t('createAgent.optional')}
          </Badge>
        </HStack>
        <Text color='app.text.secondary' fontSize='sm' mt={1}>
          {t('createAgent.exampleQuestionsHelp')}
        </Text>
        <Box mt={2} width="100%">
          <Box display="grid" gridTemplateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
            {questions.map((question, index) => (
              <ExampleQuestionCard
                key={question}
                question={editingIndex === index ? editingValue : question}
                isEditing={editingIndex === index}
                isEditable={true}
                onEditStart={() => handleEditStart(index)}
                onEditSave={() => handleEditSave(index)}
                onRemove={() => removeExampleQuestion(question)}
                onEditCancel={handleEditCancel}
                onClick={undefined}
                // Ensure consistent sizing and padding
                minH="60px"
                w="100%"
              />
            ))}
            {/* Add new question card */}
            {isAddingNew ? (
              <Box borderRadius="lg" bg={cardBg} boxShadow="md" border="1px" borderColor={borderColor}>
                <Flex>
                  <Input
                    value={newQuestionValue}
                    onChange={e => setNewQuestionValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleAddExampleQuestion()
                      if (e.key === 'Escape') { setIsAddingNew(false); setNewQuestionValue('') }
                    }}
                    autoFocus
                    placeholder={t('Type your example question')}
                    size="md"
                    borderRadius="lg"
                    borderTopRightRadius="0"
                    borderBottomRightRadius="0"
                    border='2px solid'
                    borderColor='gray.200'
                    _hover={{ borderColor: 'gray.300' }}
                    _focus={{
                      borderColor: 'primaryScheme.500',
                      boxShadow: '0 0 0 1px var(--chakra-colors-primaryScheme-500)',
                    }}
                  />
                  <IconButton
                    aria-label="Save"
                    icon={<FiArrowLeft />}
                    onClick={handleAddExampleQuestion}
                    size="md"
                    borderTopLeftRadius="0"
                    borderBottomLeftRadius="0"
                    colorScheme="primaryScheme"
                  />
                </Flex>
              </Box>
            ) : (
              <Box
                onClick={() => setIsAddingNew(true)}
                borderRadius="lg"
                bg={cardBg}
                boxShadow="md"
                border="1px"
                borderStyle="dashed"
                borderColor="gray.200"
                transition="all 0.2s"
                display="flex"
                alignItems="center"
                justifyContent="center"
                p={4}
                cursor="pointer"
                minH="60px"
                _hover={{
                  boxShadow: "lg",
                  transform: "translateY(-2px)",
                  borderColor: "primaryScheme.200"
                }}
              >
                +
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </VStack>
  )
}

export default ExampleQuestionsEditor
